/** @jsxImportSource @emotion/react */
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Text } from "../../atoms";
import { NavSectionProps } from "./types";
import * as styles from "./index.styles";

const NavSection: React.FC<NavSectionProps> = ({
  routeSection: { title, children: routes },
  isCollapsed = false,
}) => {
  const { pathname } = useLocation();

  const isPathActive = (path?: string) => {
    if (!path) return false;
    return pathname === path || pathname.startsWith(`${path}/`);
  };

  return (
    <div css={styles.root}>
      {title && !isCollapsed && (
        <div css={(theme: any) => styles.title({ theme })}>
          <Text textType="labelSm">{title}</Text>
        </div>
      )}

      <ul css={styles.list}>
        {routes.map(({ path = "", text, children, icon: Icon }) => {
          const hasActiveChild = children?.some((child) =>
            isPathActive(child.path)
          );

          const isActive = isPathActive(path) || !!hasActiveChild;

          if (!children) {
            return (
              <li
                key={`navbarSection-${text}`}
                css={(theme: any) => styles.item({ theme, isActive })}
              >
                <Link to={path} title={isCollapsed ? text : undefined}>
                  <Icon />
                  {!isCollapsed && <Text textType="sm">{text}</Text>}
                </Link>
              </li>
            );
          }

          return (
            <li key={`navbarSection-${text}`}>
              <details css={styles.details} open={!isCollapsed && isActive}>
                <summary
                  css={(theme: any) => styles.summary({ theme, isActive })}
                  title={isCollapsed ? text : undefined}
                >
                  <span className="summary-left">
                    <Icon />
                    {!isCollapsed && <Text textType="sm">{text}</Text>}
                  </span>

                  {!isCollapsed && <span className="chevron">›</span>}
                </summary>

                {!isCollapsed && (
                  <ul css={styles.subList}>
                    {children.map(({ path: childPath, text: childText }) => (
                      <li
                        key={`navbarItem-${childText}`}
                        css={(theme: any) =>
                          styles.subItem({
                            theme,
                            isActive: isPathActive(childPath),
                          })
                        }
                      >
                        <Link to={childPath}>
                          <Text textType="sm">{childText}</Text>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </details>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default NavSection;